import { Service } from 'constants/usercentrics';
import LazyLoad from 'react-lazyload';
import { sanitizeContentfulBlockId } from 'utils/contentful';
import { useTranslation } from 'hooks/useTranslation';
import clsx from 'clsx';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import { useContext, useEffect, useState } from 'react';
import { CookieContext } from 'context/cookie';
import styles from './Youtube.module.scss';
import RichText from './RichText';
import type { InspectorPropsType } from 'hooks/useLivePreview';
import type { IBlockYoutubeFields } from 'types/contentful/generated';
import type { Document } from '@contentful/rich-text-types';

type Props = IBlockYoutubeFields & {
  descriptionContent?: Document;
  inspectorProps?: InspectorPropsType;
};

const Youtube = ({
  blockId,
  title,
  description,
  descriptionContent,
  youtubeVideoId,
  alignment = 'Center',
  inspectorProps,
}: Props): JSX.Element => {
  const alignClass = {
    Left: styles.alignLeft,
    Center: styles.alignCenter,
    Right: styles.alignRight,
  };

  const { cookies } = useContext(CookieContext);

  const [allowed, setAllowed] = useState(false);

  const { locale } = useTranslation();
  const showVideoSubtitles = locale === 'de' ? 1 : 0;

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, Service.YOUTUBE);

    setAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  if (!allowed) {
    return <EmbedPlaceholder service={Service.YOUTUBE} />;
  }

  return (
    <div id={sanitizeContentfulBlockId(blockId)} className={styles.container}>
      <div className={clsx(styles.flex, alignClass[alignment])}>
        <div className={styles.wrapper}>
          {title && <h2 className={styles.headline}>{title}</h2>}
          <LazyLoad classNamePrefix="video">
            <iframe
              className={styles.iframe}
              src={`https://www.youtube.com/embed/${youtubeVideoId}?cc_load_policy=${showVideoSubtitles}&rel=0`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              {...inspectorProps?.({ fieldId: 'youtubeVideoId' })}
            />
          </LazyLoad>
          {(descriptionContent || description) && (
            <div className={styles.description}>
              <RichText fields={descriptionContent || description} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Youtube;
